import { useState } from 'react';
import api, { AUTH_TOKEN_STORAGE_KEY } from '../../api/config';
import userIcon from '../../assets/icons/user.svg';
import { usePractice } from '../../providers/practices/PracticesProvider';
import { clearPersistedQueryCache } from '../../providers/queryClient';
import { useUser } from '../../providers/UserProvider';
import { stashToast } from '../../utils/pendingToast';

export default function AdminBanner() {
    const { user, isLoading } = useUser();
    const [isResetPracticeLoading, setIsResetPracticeLoading] = useState(false);
    // The practices query's own state, not the user's: they resolve
    // independently, and `practices` is [] while this one is in flight.
    const { practices, isLoading: isPracticesLoading } = usePractice();
    const isAdmin = Boolean(user?.database?.data?.isAdmin);
    const hasPracticeIds = user?.database?.data?.practices?.length ?? 0;

    const resetPractice = async () => {
        setIsResetPracticeLoading(true);
        try {
            const { data } = await api.post(`/practices/reset`);
            // The rotated token is the only thing that busts the per-instance
            // auth cache; without it the reload is served the old practice.
            if (!data?.token) {
                throw new Error('Practice reset returned no token');
            }
            localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, data.token);
            clearPersistedQueryCache();
            window.location.reload();
        } catch (error) {
            // May have committed before failing, so recover to whatever the
            // server now says rather than trusting local state.
            stashToast('Failed to log out of the practice. Please try again.', 'error');
            clearPersistedQueryCache();
            window.location.reload();
        }
    };

    // Empty practices here means loading or a failed fetch, never
    // "impersonating nothing" - the user carries practice ids either way.
    if (!isAdmin || !hasPracticeIds || isLoading || isResetPracticeLoading || isPracticesLoading) {
        return null;
    }

    const firstPractice = practices[0];
    // A failed or empty fetch must still render: Log out lives in this banner,
    // and hiding it would strand the admin inside the impersonation.
    const label =
        practices.length === 1
            ? `Logged in as Dr. ${firstPractice?.doctorFirstName ?? ''} ${firstPractice?.doctorLastName ?? ''} (${firstPractice?.name ?? ''})`
            : practices.length > 1
              ? `Logged in with ${practices.length} practices`
              : 'Logged in as another practice';

    return (
        <div className="bg-blue-100 w-full p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
                <img src={userIcon} alt="User Icon" className="w-5 h-5" />
                <p className="font-semibold text-base text-gray-800">{label}</p>
            </div>
            <button
                onClick={() => {
                    resetPractice();
                }}
                className="rounded-lg bg-gray-800 text-white text-sm font-normal px-3 py-2 hover:bg-gray-700"
            >
                Log out
            </button>
        </div>
    );
}
